import {
    AccountView,
    BuildingContactRoleView,
    BuildingView,
    CompanyView,
    DivisionView,
    OpportunityStatusView,
    UserView
} from '@api-proxies';
import {
    IAdditionalFiltersConfig,
    IAdditionalFilterVisibilityConfig,
    IGenericAdditionalFilters
} from '../additional-filters/additional-filters.config';

export const OPPORTUNITIES_CONTACT_FILTER_KEY = 'opportunities-contact';
export const OPPORTUNITIES_BUILDING_FILTER_KEY = 'opportunities-building';
export const OPPORTUNITIES_COMPANY_FILTER_KEY = 'opportunities-company';
export const OPPORTUNITIES_ACCOUNT_FILTER_KEY = 'opportunities-account';

export type FilterValueOpportunities = IMainFiltersOpportunities & IAdditionalFiltersOpportunities;

export interface IAdditionalFiltersOpportunities extends IGenericAdditionalFilters {
    companies?: CompanyView[];
    buildings?: BuildingView[];
    accounts?: AccountView[];
    contactId?: number;
    departments?: DivisionView[];
    salespersons?: UserView[];
    statuses?: OpportunityStatusView[];
    includeArchived?: boolean;
    probabilityFrom?: number;
    probabilityTo?: number;
    potentialValueFrom?: number;
    potentialValueTo?: number;
    proposedGrossMarginFrom?: number;
    proposedGrossMarginTo?: number;
    expectedResponseDateFrom?: Date;
    expectedResponseDateTo?: Date;
    dueDateFrom?: Date;
    dueDateTo?: Date;
    companyRoles?: BuildingContactRoleView[];
}

export interface IMainFiltersOpportunities {
    searchText?: string;
    onlyFavorites?: boolean;
    sortBy?: string;
    sortDirection?: string;
}

const DEFAULT_ADDITIONAL_FILTERS_VISIBILITY: IAdditionalFilterVisibilityConfig = {
    dueDate: true,
    expectedBookingDate: true,
    account: true,
    company: true,
    companyRole: true,
    potentialValue: true,
    proposedGrossMargin: true,
    probability: true,
    department: true,
    salesperson: true,
    status: true,
    includeArchived: true
};

export const OPPORTUNITIES_FILTER_CONFIG: IAdditionalFiltersConfig<IAdditionalFiltersOpportunities> = {
    entityType: 'opportunities',

    extraConfigs: {
        company: {
            multiple: true
        }
    },

    visibility: DEFAULT_ADDITIONAL_FILTERS_VISIBILITY,

    transformToFilters: (formValue: any): IAdditionalFiltersOpportunities => {
        return {
            accounts: formValue.accounts && formValue.accounts.length > 0 ? formValue.accounts : undefined,
            expectedResponseDateFrom: formValue.expectedDateRange?.from,
            expectedResponseDateTo: formValue.expectedDateRange?.to,
            dueDateFrom: formValue.dueDateRange?.from,
            dueDateTo: formValue.dueDateRange?.to,
            potentialValueFrom: formValue.potentialValueRange?.from,
            potentialValueTo: formValue.potentialValueRange?.to,
            proposedGrossMarginFrom: formValue.potentialGrossMarginRange?.from,
            proposedGrossMarginTo: formValue.potentialGrossMarginRange?.to,
            probabilityFrom: formValue.probabilityRange?.from,
            probabilityTo: formValue.probabilityRange?.to,
            departments: formValue.departments && formValue.departments.length > 0 ? formValue.departments : undefined,
            salespersons:
                formValue.salespersons && formValue.salespersons.length > 0 ? formValue.salespersons : undefined,
            statuses:
                formValue.statuses && formValue.statuses.length > 0
                    ? formValue.statuses.map((item: any) => ({ id: item.id, name: item.name }) as OpportunityStatusView)
                    : undefined,
            companies: formValue.companies && formValue.companies.length > 0 ? formValue.companies : undefined,
            companyRoles:
                formValue.companyRoles && formValue.companyRoles.length > 0 ? formValue.companyRoles : undefined,
            opportunityStatuses:
                formValue.opportunityStatuses && formValue.opportunityStatuses.length > 0
                    ? formValue.opportunityStatuses
                    : undefined,
            includeArchived: formValue.includeArchived || undefined
        };
    },

    transformFromFilters: (filters: IAdditionalFiltersOpportunities): any => {
        return {
            accounts: filters?.accounts || [],
            expectedDateRange:
                filters?.expectedResponseDateFrom || filters?.expectedResponseDateTo
                    ? { from: filters?.expectedResponseDateFrom, to: filters?.expectedResponseDateTo }
                    : { from: undefined, to: undefined },
            dueDateRange:
                filters?.dueDateFrom || filters?.dueDateTo
                    ? { from: filters?.dueDateFrom, to: filters?.dueDateTo }
                    : { from: undefined, to: undefined },
            potentialValueRange:
                filters?.potentialValueFrom || filters?.potentialValueTo
                    ? { from: filters?.potentialValueFrom, to: filters?.potentialValueTo }
                    : { from: undefined, to: undefined },
            potentialGrossMarginRange:
                filters?.proposedGrossMarginFrom || filters?.proposedGrossMarginTo
                    ? { from: filters?.proposedGrossMarginFrom, to: filters?.proposedGrossMarginTo }
                    : { from: undefined, to: undefined },
            probabilityRange:
                filters?.probabilityFrom || filters?.probabilityTo
                    ? { from: filters?.probabilityFrom, to: filters?.probabilityTo }
                    : { from: undefined, to: undefined },
            departments: filters?.departments || [],
            salespersons: filters?.salespersons || [],
            statuses: filters?.statuses?.map((status) => ({ id: status.id, name: status.name || '' })) || [],
            companies: filters?.companies || [],
            companyRoles: filters?.companyRoles || [],
            opportunityStatuses: filters?.statuses || [],
            includeArchived: filters?.includeArchived || undefined
        };
    },

    hasFiltersApplied: (filters: IAdditionalFiltersOpportunities | null): boolean => {
        if (!filters) return false;

        const arrayProps = ['accounts', 'companies', 'companyRoles', 'departments', 'salespersons', 'statuses'];
        if (arrayProps.some((prop) => hasArrayItems((filters as any)[prop]))) return true;

        const rangeProps = [
            'expectedResponseDateFrom',
            'expectedResponseDateTo',
            'dueDateFrom',
            'dueDateTo',
            'potentialValueFrom',
            'potentialValueTo',
            'proposedGrossMarginFrom',
            'proposedGrossMarginTo',
            'probabilityFrom',
            'probabilityTo'
        ];
        if (rangeProps.some((prop) => (filters as any)[prop])) return true;

        return filters.includeArchived === true;
    }
};

function hasArrayItems<T>(array?: T[]): boolean {
    return !!(array && array.length > 0);
}
